/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import { BrandColors, MaterialIcon, Text, Typography } from '@blend/enterprise-components';

import React from 'react';
import { NotaryAPI } from '../../../api/notary/types';
import { HighlightText } from '../../../components/HighlightText/HighlightText';
import { formatFullName } from '@utils/formatting';

const styles = StyleSheet.create({
  wrapper: {
    ...Typography.Roboto,
    padding: '8px 16px',
    fontWeight: Typography.BOOK,
    display: 'inline-block',
    width: '100%',
  },
  searchFields: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    overflow: 'hidden',
  },
  field: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    overflow: 'hidden',
  },
});

export namespace RoomsPipelineSearchResult {
  export interface Props {
    result: NotaryAPI.RoomListItem;
    highlight: string;
  }
}

export const PipelineSearchResult: React.FC<RoomsPipelineSearchResult.Props> = ({
  result: { loanNumber, signers, externalOrganization, scheduleDate },
  highlight,
}) => {
  const getBorrower = () => {
    let borrower = signers.find(signer => signer.title === 'Primary Borrower');
    if (!borrower) {
      borrower = signers.find(signer => signer.title === 'Borrower');
    }
    return borrower ?? signers[0];
  };

  const borrower = getBorrower();
  const additionalSigners = signers.length > 1 ? `+${signers.length - 1}` : '';
  const borrowerFullName = borrower ? `${formatFullName(borrower)} ${additionalSigners}` : '';

  return (
    <div data-it={`search-result-${loanNumber}`} className={css(styles.wrapper)}>
      {borrower ? (
        <HighlightText
          color={BrandColors.NEUTRAL_800}
          data-it="search-result-signers"
          fontSize="md"
          displayBlock
          highlight={highlight}
          text={borrowerFullName}
        />
      ) : (
        <Text color={BrandColors.NEUTRAL_800} data-it="search-result-no-signers" fontSize="md" displayBlock>
          No Signers
        </Text>
      )}
      <div className={css(styles.searchFields)}>
        <div className={css(styles.field)}>
          <MaterialIcon color={BrandColors.NEUTRAL_700} size={12} type="date_range" />
          <Text
            color={BrandColors.NEUTRAL_700}
            data-it="search-result-scheduleDate"
            fontSize="sm"
            displayBlock
            whiteSpace="nowrap"
            ellipsis
          >
            {scheduleDate}
          </Text>
        </div>
        {loanNumber && (
          <div className={css(styles.field)}>
            <Text fontSize="sm">{'# '}</Text>
            <HighlightText
              displayBlock
              color={BrandColors.NEUTRAL_700}
              data-it="LoanSearchResult"
              fontSize="sm"
              text={loanNumber || ''}
              highlight={highlight}
            />
          </div>
        )}
        {externalOrganization && (
          <div className={css(styles.field)}>
            <MaterialIcon color={BrandColors.NEUTRAL_700} size={12} type="domain" />
            <Text
              color={BrandColors.NEUTRAL_700}
              data-it="search-result-externalOrganization"
              fontSize="sm"
              displayBlock
              whiteSpace="nowrap"
              ellipsis
            >
              {externalOrganization}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};
